/**
 * /profile — your settings.
 *
 * Three sections:
 *   1. Identity      — display name, accent hue, theme, photo.
 *   2. Schedule      — working hours editor + scheduling prefs (the
 *                      "always allow / ask / never" matrix the user
 *                      asked for, per work level) + connected
 *                      calendars + outbound feed URL.
 *   3. Workspace     — link out to /tenant.
 *
 * Section 2 is the new home for the calendar feature. Everything
 * lives in one place so a user has ONE settings surface for the
 * "how SynFolio drives my day" controls.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  type SchedulingPref,
  type CalendarShareInput,
  type CalendarShareLens,
  type CalendarShareSummary,
  type CalendarSharePreviewEvent,
  type CalendarSharePreviewTodo,
} from '../api';
import type { BoardSummary } from '../../shared/types';
import { Avatar } from '../components/Avatar';
import { AvatarEditor } from '../components/AvatarEditor';
import { UiNode } from '../lib/UiNode';
import { THEMES, applyTheme } from '../lib/themes';
import { useToast } from '../components/Toast';
import {
  IconCalendar,
  IconClock,
  IconCopy,
  IconTrash,
  IconPlus,
  IconShare,
  IconEye,
} from '../components/Icon';
import type { User } from '../../shared/types';
import { Link } from '../router';

export function ProfileScreen({
  user,
  onUserChange,
}: {
  user: User;
  onUserChange: (u: User) => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-medium tracking-tight">Settings</h1>
          <p className="mt-1 text-sm text-cream-3">
            Identity, calendar, and how SynFolio schedules your time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/calendar" className="btn btn-ghost text-xs">
            Calendar
          </Link>
          <Link to="/tenant" className="btn btn-ghost text-xs">
            Workspace
          </Link>
          <Link to="/" className="btn btn-ghost text-xs">
            Back home
          </Link>
        </div>
      </header>

      <IdentitySection user={user} onUserChange={onUserChange} />
      <CalendarsSection />
      <WorkingHoursSection />
      <SchedulingPrefsSection />
      <OutboundFeedSection />
      <CalendarSharesSection />
    </div>
  );
}

// ---- Identity ------------------------------------------------------------

function IdentitySection({
  user,
  onUserChange,
}: {
  user: User;
  onUserChange: (u: User) => void;
}) {
  const [name, setName] = useState(user.name);
  const [hue, setHue] = useState(user.avatarHue);
  const [themePref, setThemePref] = useState<string>(user.themePreference ?? 'follow_board');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState(false);

  const dirty =
    name.trim() !== user.name ||
    hue !== user.avatarHue ||
    themePref !== (user.themePreference ?? 'follow_board');

  async function save() {
    if (!dirty || busy) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const trimmed = name.trim();
      const prefChanged = themePref !== (user.themePreference ?? 'follow_board');
      const r = await api.patchProfile({
        name: trimmed !== user.name ? trimmed : undefined,
        avatarHue: hue !== user.avatarHue ? hue : undefined,
        ...(prefChanged ? { themePreference: themePref } : {}),
      });
      onUserChange(r.user);
      applyTheme({ userPreference: r.user.themePreference ?? null });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <UiNode
        as="section"
        name="profile-identity"
        entity="user"
        className="panel flex items-start gap-6 p-5"
      >
        <div className="flex flex-shrink-0 flex-col items-center gap-2">
          <Avatar user={{ ...user, name, avatarHue: hue }} size={96} />
          <UiNode
            as="button"
            name="profile-edit-photo-button"
            entity="user"
            action="edit_photo_open"
            type="button"
            className="btn btn-ghost text-xs"
            onClick={() => setEditingAvatar(true)}
          >
            {user.avatarUrl ? 'Change photo' : 'Add photo'}
          </UiNode>
        </div>
        <form
          className="flex flex-1 flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <label className="flex flex-col gap-1.5 text-xs text-cream-3">
            Display name
            <UiNode
              as="input"
              name="profile-name-input"
              entity="user"
              action="edit_name"
              className="input text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
            />
          </label>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-cream-3">Accent hue · {hue}°</label>
            <UiNode
              as="input"
              name="profile-hue-slider"
              entity="user"
              action="edit_hue"
              type="range"
              min={0}
              max={359}
              value={hue}
              onChange={(e) => setHue(Number(e.target.value))}
            />
            <div className="mt-1 flex flex-wrap gap-1.5">
              {[18, 28, 60, 150, 200, 260, 320].map((h) => (
                <button
                  key={h}
                  type="button"
                  className="h-6 w-6 rounded-full border border-ink-rim"
                  style={{ background: `hsl(${h}, 70%, 55%)` }}
                  onClick={() => setHue(h)}
                  aria-label={`Set hue ${h}`}
                />
              ))}
            </div>
          </div>
          <label className="flex flex-col gap-1.5 text-xs text-cream-3">
            Theme
            <UiNode
              as="select"
              name="profile-theme-select"
              entity="user"
              action="edit_theme_preference"
              className="input text-sm"
              value={themePref}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const v = e.target.value;
                setThemePref(v);
                applyTheme({ userPreference: v });
              }}
            >
              <option value="follow_board">Follow each board's theme</option>
              <option value="system">Follow my system (light / dark)</option>
              {THEMES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label} — {t.description}
                </option>
              ))}
            </UiNode>
          </label>
          {error && <p className="text-xs text-rust">{error}</p>}
          <div className="flex items-center gap-3">
            <UiNode
              as="button"
              name="profile-save-button"
              entity="user"
              action="save"
              state={busy ? 'busy' : 'idle'}
              type="submit"
              className="btn btn-primary"
              disabled={!dirty || busy || !name.trim()}
            >
              {busy ? 'Saving…' : 'Save changes'}
            </UiNode>
            {saved && <span className="text-xs text-leaf">Saved ✓</span>}
          </div>
        </form>
      </UiNode>

      {editingAvatar && (
        <AvatarEditor
          initialUrl={user.avatarUrl}
          onClose={() => setEditingAvatar(false)}
          onSaved={(url) => {
            onUserChange({ ...user, avatarUrl: url });
            setEditingAvatar(false);
          }}
        />
      )}
    </>
  );
}

// ---- Calendars (linked feeds) -------------------------------------------

interface LinkRow {
  id: string;
  provider: 'ics' | 'google' | 'microsoft';
  displayName: string | null;
  status: 'active' | 'error' | 'revoked';
  lastError: string | null;
  lastSyncedAt: number | null;
  createdAt: number;
}

function CalendarsSection() {
  const [links, setLinks] = useState<LinkRow[] | null>(null);
  const [addUrl, setAddUrl] = useState('');
  const [addLabel, setAddLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauth, setOauth] = useState<{ google: boolean; microsoft: boolean }>(
    () => ({ google: false, microsoft: false }),
  );
  const { toast } = useToast();

  async function refresh() {
    try {
      const [linksRes, oauthRes] = await Promise.all([
        api.listCalendars(),
        api.oauthStatus().catch(() => ({ google: false, microsoft: false })),
      ]);
      setLinks(linksRes.links);
      setOauth(oauthRes);
    } catch (e) {
      setError(describeError(e));
    }
  }
  useEffect(() => {
    void refresh();
    // Surface ?calendar=… query bounces from the OAuth callback.
    const params = new URLSearchParams(window.location.search);
    const c = params.get('calendar');
    if (c === 'connected') {
      toast({ tone: 'success', title: 'Calendar connected', body: 'Events syncing now.' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (c === 'cancelled') {
      toast({ title: 'Connection cancelled' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (c === 'err') {
      const reason = params.get('reason') ?? 'Unknown error';
      toast({ tone: 'warning', title: 'Connection failed', body: reason });
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function addIcs(e: React.FormEvent) {
    e.preventDefault();
    if (!addUrl.trim() || adding) return;
    setAdding(true);
    setError(null);
    try {
      const r = await api.addIcsCalendar(addUrl.trim(), addLabel.trim() || undefined);
      setAddUrl('');
      setAddLabel('');
      await refresh();
      if (!r.ok) {
        toast({
          tone: 'warning',
          title: 'Added — but the first sync failed',
          body: r.reason ?? 'We\'ll retry on the next sync cycle.',
        });
      } else {
        toast({ tone: 'success', title: 'Calendar added', body: 'Synced and ready.' });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setAdding(false);
    }
  }

  async function sync(id: string) {
    const r = await api.syncCalendar(id);
    await refresh();
    if (!r.ok) {
      toast({ tone: 'warning', title: 'Sync failed', body: r.reason ?? undefined });
    } else {
      toast({ tone: 'success', title: 'Synced' });
    }
  }

  async function remove(id: string, label: string) {
    if (!confirm(`Disconnect ${label}?`)) return;
    await api.deleteCalendar(id);
    await refresh();
  }

  return (
    <section className="panel flex flex-col gap-3 p-5">
      <div className="flex items-center gap-2">
        <IconCalendar size={16} />
        <h2 className="text-sm font-medium">Calendars</h2>
      </div>
      <p className="text-xs text-cream-3">
        Connect calendars you don't want us scheduling over. Busy events are read-only —
        we never write back. Only your own calendars feed your scheduler.
      </p>

      {/* OAuth providers — enabled when the server has their secrets. */}
      <div className="grid gap-2 sm:grid-cols-2">
        <OauthProviderCard
          name="Google Calendar"
          enabled={oauth.google}
          href="/api/calendars/oauth/google/start"
        />
        <OauthProviderCard
          name="Outlook / Microsoft"
          enabled={oauth.microsoft}
          href="/api/calendars/oauth/microsoft/start"
        />
      </div>

      {/* Existing links */}
      {error && !links ? (
        <div className="rounded-lg border border-rust/40 bg-rust/5 p-2.5 text-xs text-rust">
          Couldn't load your calendars: {error}.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              setError(null);
              void refresh();
            }}
          >
            Try again
          </button>
        </div>
      ) : !links ? (
        <p className="text-xs text-cream-3">Loading…</p>
      ) : links.length === 0 ? (
        <p className="text-xs text-cream-3">No calendars yet. Paste an ICS URL below to begin.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {links.map((l) => (
            <li
              key={l.id}
              className="flex flex-wrap items-center gap-2 rounded-lg bg-ink-3 p-2.5"
            >
              <span
                className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${
                  l.status === 'active'
                    ? 'bg-leaf'
                    : l.status === 'error'
                      ? 'bg-rust'
                      : 'bg-ash'
                }`}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {l.displayName ?? '(calendar)'}
                </div>
                <div className="truncate text-[11px] text-cream-3">
                  {l.provider.toUpperCase()} ·{' '}
                  {l.lastSyncedAt
                    ? `synced ${relativeSeen(l.lastSyncedAt)}`
                    : 'not yet synced'}
                  {l.status === 'error' && l.lastError ? ` · ${l.lastError}` : ''}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost !px-2 !py-1 text-[11px]"
                onClick={() => void sync(l.id)}
              >
                Re-sync
              </button>
              <button
                type="button"
                className="btn btn-ghost !px-2 !py-1 text-[11px] text-rust"
                onClick={() => void remove(l.id, l.displayName ?? 'this calendar')}
                aria-label="Disconnect"
              >
                <IconTrash size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add ICS */}
      <form
        className="mt-2 flex flex-col gap-2 rounded-xl border border-ink-rim bg-ink-3/60 p-3 sm:flex-row"
        onSubmit={addIcs}
      >
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <input
            type="url"
            className="input flex-1 text-sm"
            value={addUrl}
            onChange={(e) => setAddUrl(e.target.value)}
            placeholder="https://… or webcal://… ICS feed URL"
          />
          <input
            type="text"
            className="input sm:w-48 text-sm"
            value={addLabel}
            onChange={(e) => setAddLabel(e.target.value)}
            placeholder="Label (optional)"
            maxLength={80}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary inline-flex items-center justify-center gap-1.5 text-xs"
          disabled={adding || !addUrl.trim()}
        >
          <IconPlus size={12} />
          {adding ? 'Adding…' : 'Add feed'}
        </button>
      </form>
      <p className="text-[10px] text-cream-3">
        Find the ICS URL in your calendar's "Share" or "Subscribe" settings. iCloud:
        Calendar Settings → Make Public Calendar → copy URL.
      </p>
      {error && <p className="text-xs text-rust">{error}</p>}
    </section>
  );
}

function OauthProviderCard({
  name,
  enabled,
  href,
}: {
  name: string;
  enabled: boolean;
  href: string;
}) {
  return (
    <div
      className={`flex items-center justify-between rounded-lg border p-3 text-xs ${
        enabled
          ? 'border-ink-rim bg-ink-3'
          : 'border-dashed border-ink-rim bg-ink-3/40'
      }`}
    >
      <div>
        <div className="font-medium text-cream-2">{name}</div>
        <div className="text-[11px] text-cream-3">
          {enabled ? 'Click Connect to authorise read-only access' : 'OAuth not configured on this server'}
        </div>
      </div>
      {enabled ? (
        <a href={href} className="btn btn-primary text-[11px]">
          Connect
        </a>
      ) : (
        <button type="button" className="btn btn-ghost text-[11px]" disabled>
          Connect
        </button>
      )}
    </div>
  );
}

// ---- Working hours -------------------------------------------------------

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function WorkingHoursSection() {
  const [tz, setTz] = useState<string>(() => guessTz());
  const [days, setDays] = useState<Map<number, { start: number; end: number }>>(
    new Map(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.getWorkingHours();
        if (r.timezone) setTz(r.timezone);
        const m = new Map<number, { start: number; end: number }>();
        for (const d of r.days) m.set(d.dayOfWeek, { start: d.startMinute, end: d.endMinute });
        setDays(m);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  function toggleDay(dow: number) {
    setDays((cur) => {
      const next = new Map(cur);
      if (next.has(dow)) next.delete(dow);
      else next.set(dow, { start: 9 * 60, end: 18 * 60 });
      return next;
    });
  }
  function updateDay(dow: number, field: 'start' | 'end', minute: number) {
    setDays((cur) => {
      const next = new Map(cur);
      const existing = next.get(dow);
      if (!existing) return cur;
      next.set(dow, { ...existing, [field]: minute });
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.putWorkingHours({
        timezone: tz,
        days: Array.from(days.entries()).map(([dow, { start, end }]) => ({
          dayOfWeek: dow,
          startMinute: start,
          endMinute: end,
        })),
      });
      toast({ tone: 'success', title: 'Working hours saved' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel flex flex-col gap-3 p-5">
      <div className="flex items-center gap-2">
        <IconClock size={16} />
        <h2 className="text-sm font-medium">Working hours</h2>
      </div>
      <p className="text-xs text-cream-3">
        When SynFolio is allowed to put things on your calendar. Outside these windows
        you're off-the-clock.
      </p>
      <label className="flex flex-col gap-1.5 text-xs text-cream-3">
        Timezone
        <input
          className="input text-sm sm:max-w-xs"
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          placeholder="Europe/London"
          list="tz-suggestions"
        />
        <datalist id="tz-suggestions">
          {COMMON_TZS.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
      </label>

      <div className="grid gap-1.5">
        {[1, 2, 3, 4, 5, 6, 0].map((dow) => {
          const row = days.get(dow);
          const active = !!row;
          return (
            <div
              key={dow}
              className={`flex flex-wrap items-center gap-2 rounded-lg border p-2.5 text-sm ${
                active ? 'border-ink-rim bg-ink-3' : 'border-dashed border-ink-rim/60 bg-ink-3/40'
              }`}
            >
              <label className="flex w-14 items-center gap-2 text-xs text-cream-2">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleDay(dow)}
                  className="h-4 w-4 accent-[color:var(--color-clove)]"
                />
                {DAY_NAMES[dow]}
              </label>
              {active && row ? (
                <div className="flex flex-1 items-center gap-2">
                  <TimePicker
                    minute={row.start}
                    onChange={(m) => updateDay(dow, 'start', m)}
                    label="Start"
                  />
                  <span className="text-cream-3">→</span>
                  <TimePicker
                    minute={row.end}
                    onChange={(m) => updateDay(dow, 'end', m)}
                    label="End"
                  />
                </div>
              ) : (
                <span className="text-xs text-cream-3">Off</span>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="btn btn-ghost text-xs"
          onClick={() => {
            // Sensible default: 9–18 Mon–Fri.
            const m = new Map<number, { start: number; end: number }>();
            [1, 2, 3, 4, 5].forEach((d) => m.set(d, { start: 9 * 60, end: 18 * 60 }));
            setDays(m);
          }}
        >
          Reset to 9–18 weekdays
        </button>
        <button
          type="button"
          className="btn btn-primary text-xs"
          onClick={save}
          disabled={busy || !loaded}
        >
          {busy ? 'Saving…' : 'Save hours'}
        </button>
      </div>
      {error && <p className="text-xs text-rust">{error}</p>}
    </section>
  );
}

function TimePicker({
  minute,
  onChange,
  label,
}: {
  minute: number;
  onChange: (m: number) => void;
  label: string;
}) {
  // 15-minute resolution is plenty for "when am I working" and keeps
  // the dropdown short.
  const options = useMemo(() => {
    const r: number[] = [];
    for (let m = 0; m <= 24 * 60; m += 15) r.push(m);
    return r;
  }, []);
  return (
    <select
      className="input text-xs sm:text-sm"
      value={minute}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label={label}
    >
      {options.map((m) => (
        <option key={m} value={m}>
          {formatMinute(m)}
        </option>
      ))}
    </select>
  );
}

// ---- Scheduling preferences ---------------------------------------------

function SchedulingPrefsSection() {
  const [prefs, setPrefs] = useState<SchedulingPref[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  async function load() {
    try {
      setError(null);
      const r = await api.getSchedulingPrefs();
      setPrefs(r.prefs);
    } catch (e) {
      setError(describeError(e));
    }
  }
  useEffect(() => {
    void load();
  }, []);

  function update(level: SchedulingPref['workLevel'], patch: Partial<SchedulingPref>) {
    setPrefs((cur) =>
      (cur ?? []).map((p) => (p.workLevel === level ? { ...p, ...patch } : p)),
    );
  }

  async function save() {
    if (!prefs) return;
    setBusy(true);
    setError(null);
    try {
      await api.putSchedulingPrefs(prefs);
      toast({ tone: 'success', title: 'Preferences saved' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  const labels: Record<SchedulingPref['workLevel'], { name: string; hint: string }> = {
    deep: {
      name: 'Deep work',
      hint: 'Focus-heavy work that benefits from a 90+ minute uninterrupted block.',
    },
    shallow: {
      name: 'Shallow work',
      hint: 'Context-switchable: emails, light reviews, planning, short calls.',
    },
    admin: {
      name: 'Admin',
      hint: 'Quick wins that slot into 15-minute gaps between meetings.',
    },
  };

  return (
    <section className="panel flex flex-col gap-3 p-5">
      <div className="flex items-center gap-2">
        <IconCalendar size={16} />
        <h2 className="text-sm font-medium">Auto-scheduling</h2>
      </div>
      <p className="text-xs text-cream-3">
        For each kind of work, decide whether SynFolio puts new tasks on your calendar
        automatically, asks first, or leaves them in your inbox.
      </p>
      {error && !prefs ? (
        <div className="rounded-lg border border-rust/40 bg-rust/5 p-2.5 text-xs text-rust">
          Couldn't load your scheduling preferences: {error}.{' '}
          <button type="button" className="underline" onClick={() => void load()}>
            Try again
          </button>
        </div>
      ) : !prefs ? (
        <p className="text-xs text-cream-3">Loading…</p>
      ) : (
        <div className="flex flex-col gap-3">
          {prefs.map((p) => (
            <div
              key={p.workLevel}
              className="flex flex-col gap-2 rounded-xl border border-ink-rim bg-ink-3 p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">{labels[p.workLevel].name}</div>
                  <div className="text-[11px] text-cream-3">{labels[p.workLevel].hint}</div>
                </div>
                <div role="radiogroup" aria-label={`${labels[p.workLevel].name} mode`} className="flex rounded-lg bg-ink-2 p-0.5 text-xs">
                  {(['always', 'ask', 'never'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      role="radio"
                      aria-checked={p.mode === m}
                      className={`rounded-md px-2.5 py-1 text-xs transition ${
                        p.mode === m
                          ? 'bg-clove/20 text-clove-2'
                          : 'text-cream-3 hover:text-cream'
                      }`}
                      onClick={() => update(p.workLevel, { mode: m })}
                    >
                      {m === 'always'
                        ? 'Schedule it'
                        : m === 'ask'
                          ? 'Ask first'
                          : 'Inbox only'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-[11px] text-cream-3">
                  Default duration · {p.defaultMinutes} min
                  <input
                    type="range"
                    min={5}
                    max={p.workLevel === 'admin' ? 60 : 240}
                    step={5}
                    value={p.defaultMinutes}
                    onChange={(e) => update(p.workLevel, { defaultMinutes: Number(e.target.value) })}
                  />
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-cream-3">
                  Min block · {p.minBlockMinutes} min
                  <input
                    type="range"
                    min={5}
                    max={p.workLevel === 'admin' ? 60 : 240}
                    step={5}
                    value={p.minBlockMinutes}
                    onChange={(e) => update(p.workLevel, { minBlockMinutes: Number(e.target.value) })}
                  />
                </label>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-end">
            <button
              type="button"
              className="btn btn-primary text-xs"
              onClick={save}
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Save preferences'}
            </button>
          </div>
        </div>
      )}
      {error && <p className="text-xs text-rust">{error}</p>}
    </section>
  );
}

// ---- Outbound feed -------------------------------------------------------

function OutboundFeedSection() {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    void (async () => {
      const r = await api.getCalendarFeed();
      setUrl(r.url);
    })();
  }, []);

  async function generate() {
    setBusy(true);
    try {
      const r = await api.rotateCalendarFeed();
      setUrl(r.url);
      toast({ tone: 'success', title: 'Subscribe URL ready', body: 'Paste it into your calendar app.' });
    } finally {
      setBusy(false);
    }
  }

  async function rotate() {
    if (!confirm('Rotating the URL breaks any existing subscriptions. Continue?')) return;
    await generate();
  }

  function copy() {
    if (!url) return;
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  }

  return (
    <section className="panel flex flex-col gap-3 p-5">
      <div className="flex items-center gap-2">
        <IconCalendar size={16} />
        <h2 className="text-sm font-medium">Subscribe to your SynFolio calendar</h2>
      </div>
      <p className="text-xs text-cream-3">
        A read-only iCal feed of your scheduled SynFolio sessions and tasks. Paste this
        URL into iPhone Calendar, Outlook, or Google Calendar's "From URL".
      </p>
      {url ? (
        <div className="flex flex-wrap items-center gap-2">
          <code className="flex-1 truncate rounded-md bg-ink-3 px-3 py-2 text-xs">
            {url}
          </code>
          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
            onClick={copy}
          >
            <IconCopy size={12} />
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            type="button"
            className="btn btn-ghost text-xs"
            onClick={() => void rotate()}
            disabled={busy}
          >
            Rotate
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-primary text-xs self-start"
          onClick={() => void generate()}
          disabled={busy}
        >
          {busy ? 'Generating…' : 'Generate subscribe URL'}
        </button>
      )}
      <p className="text-[10px] text-cream-3">
        Keep this URL private — anyone with it can read your schedule. Hit "Rotate" to
        revoke the previous one.
      </p>
    </section>
  );
}

// ---- Per-recipient calendar shares ---------------------------------------

function CalendarSharesSection() {
  const [shares, setShares] = useState<CalendarShareSummary[] | null>(null);
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    void (async () => {
      const [s, b] = await Promise.all([api.listCalendarShares(), api.myBoards()]);
      setShares(s.shares);
      setBoards(b.boards);
    })();
  }, []);

  // ?focus=shares scrolls the panel into view and pops the composer
  // straight open. Linked from the Calendar screen's Share button so
  // the feature isn't buried five sections deep on Profile.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('focus') !== 'shares') return;
    const el = document.getElementById('share-with-someone');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setComposerOpen(true);
    }
  }, []);

  async function refresh() {
    const s = await api.listCalendarShares();
    setShares(s.shares);
  }

  async function onCreated() {
    setComposerOpen(false);
    await refresh();
    toast({
      tone: 'success',
      title: 'Share link created',
      body: 'Copy it and send it to the person you want to share with.',
    });
  }

  async function onRevoke(id: string, label: string) {
    if (!confirm(`Revoke "${label}"? Anyone subscribed will lose access.`)) return;
    await api.revokeCalendarShare(id);
    await refresh();
    toast({ tone: 'neutral', title: 'Share revoked', body: `"${label}" is no longer accessible.` });
  }

  return (
    <section id="share-with-someone" className="panel flex flex-col gap-3 p-5 scroll-mt-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <IconShare size={16} />
          <div>
            <h2 className="text-sm font-medium">Share with someone</h2>
            <p className="mt-0.5 text-xs text-cream-3">
              A separate link per person. Pick what they see — busy/free only, the headlines, or
              full detail.
            </p>
          </div>
        </div>
        {!composerOpen && (
          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
            onClick={() => setComposerOpen(true)}
          >
            <IconPlus size={12} />
            New share
          </button>
        )}
      </div>

      {composerOpen && (
        <ShareComposer
          boards={boards}
          onCancel={() => setComposerOpen(false)}
          onCreated={onCreated}
        />
      )}

      {shares === null ? (
        <p className="text-xs text-cream-3">Loading…</p>
      ) : shares.length === 0 && !composerOpen ? (
        <p className="text-xs text-cream-3">
          No shares yet. Use this when you want one person — Clay, the family group, an investor —
          to see a tailored slice of your calendar.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {shares.map((s) => (
            <ShareRow key={s.id} share={s} boards={boards} onRevoke={onRevoke} />
          ))}
        </ul>
      )}
    </section>
  );
}

const LENS_OPTIONS: Array<{
  value: CalendarShareLens;
  label: string;
  hint: string;
  comingSoon?: boolean;
}> = [
  {
    value: 'busy',
    label: 'Busy only',
    hint: 'Shows blocked time as "Busy" with no titles or detail. Best for casual sharing.',
  },
  {
    value: 'headlines',
    label: 'Headlines',
    hint: 'Only the milestone sessions (kickoff, demo, start/end days). Great for stakeholders.',
  },
  {
    value: 'detail',
    label: 'Full detail',
    hint: 'Titles, descriptions, and links back into SynFolio. Use with collaborators.',
  },
  {
    value: 'participant',
    label: 'Their slice (coming soon)',
    hint: 'Only items they are attending. Falls back to "Full detail" today.',
    comingSoon: true,
  },
];

const EXPIRY_PRESETS: Array<{ value: number | null; label: string }> = [
  { value: null, label: 'Never' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
];

function ShareComposer({
  boards,
  onCancel,
  onCreated,
}: {
  boards: BoardSummary[];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState('');
  const [audience, setAudience] = useState('');
  const [lens, setLens] = useState<CalendarShareLens>('busy');
  const [includeTasks, setIncludeTasks] = useState(true);
  const [includeDescriptions, setIncludeDescriptions] = useState(true);
  const [includeUrls, setIncludeUrls] = useState(true);
  // null = "all boards". Otherwise specific board ids.
  const [restrictBoards, setRestrictBoards] = useState(false);
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lensIsBusy = lens === 'busy';

  function toggleBoard(id: string) {
    setSelectedBoardIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body: CalendarShareInput = {
        label: label.trim(),
        audience: audience.trim() || undefined,
        lens,
        includeTasks: lensIsBusy ? false : includeTasks,
        includeDescriptions: lensIsBusy ? false : includeDescriptions,
        includeUrls: lensIsBusy ? false : includeUrls,
        boardIds: restrictBoards ? selectedBoardIds : null,
        expiresInDays,
      };
      await api.createCalendarShare(body);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create share');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-xl border border-ink-rim bg-ink-3/40 p-4"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-xs">
          <span className="font-medium">Label</span>
          <input
            className="input"
            placeholder="Clay, or Family WhatsApp"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={120}
            autoFocus
          />
          <span className="text-[10px] text-cream-3">Just for you. Not shown to the recipient.</span>
        </label>
        <label className="flex flex-col gap-1.5 text-xs">
          <span className="font-medium">Who's it for</span>
          <input
            className="input"
            type="email"
            placeholder="clay@example.com (optional)"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            maxLength={200}
          />
          <span className="text-[10px] text-cream-3">
            We'll show this in the dashboard so you can find the link later.
          </span>
        </label>
      </div>

      <fieldset className="flex flex-col gap-2 text-xs">
        <legend className="font-medium">Detail lens</legend>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {LENS_OPTIONS.map((o) => (
            <label
              key={o.value}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border border-ink-rim bg-ink-2 p-2.5 transition hover:border-cream-4 ${
                lens === o.value ? 'border-clove ring-1 ring-clove' : ''
              } ${o.comingSoon ? 'opacity-70' : ''}`}
            >
              <input
                type="radio"
                name="lens"
                className="mt-0.5"
                value={o.value}
                checked={lens === o.value}
                onChange={() => setLens(o.value)}
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">{o.label}</span>
                <span className="text-[10px] text-cream-3">{o.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {!lensIsBusy && (
        <fieldset className="flex flex-col gap-1.5 text-xs">
          <legend className="font-medium">What to include</legend>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeDescriptions}
              onChange={(e) => setIncludeDescriptions(e.target.checked)}
            />
            <span>Descriptions / agenda</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeUrls}
              onChange={(e) => setIncludeUrls(e.target.checked)}
            />
            <span>Links back to SynFolio (lets them open the discussion)</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeTasks}
              onChange={(e) => setIncludeTasks(e.target.checked)}
            />
            <span>Tasks (your scheduled time blocks + action items)</span>
          </label>
        </fieldset>
      )}

      <fieldset className="flex flex-col gap-1.5 text-xs">
        <legend className="font-medium">Boards</legend>
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name="board-scope"
            checked={!restrictBoards}
            onChange={() => setRestrictBoards(false)}
          />
          <span>Everything I'm a member of</span>
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name="board-scope"
            checked={restrictBoards}
            onChange={() => setRestrictBoards(true)}
          />
          <span>Just these boards</span>
        </label>
        {restrictBoards && (
          <div className="ml-5 mt-1 grid max-h-40 grid-cols-1 gap-1 overflow-auto rounded-md border border-ink-rim bg-ink-2 p-2 sm:grid-cols-2">
            {boards.length === 0 ? (
              <span className="text-cream-3">No boards yet.</span>
            ) : (
              boards.map((b) => (
                <label key={b.id} className="inline-flex items-center gap-2 text-[11px]">
                  <input
                    type="checkbox"
                    checked={selectedBoardIds.includes(b.id)}
                    onChange={() => toggleBoard(b.id)}
                  />
                  <span className="truncate">{b.name}</span>
                </label>
              ))
            )}
          </div>
        )}
      </fieldset>

      <label className="flex flex-col gap-1.5 text-xs">
        <span className="font-medium">Auto-expire</span>
        <select
          className="input"
          value={expiresInDays === null ? '' : String(expiresInDays)}
          onChange={(e) => setExpiresInDays(e.target.value === '' ? null : Number(e.target.value))}
        >
          {EXPIRY_PRESETS.map((p) => (
            <option key={p.label} value={p.value === null ? '' : p.value}>
              {p.label}
            </option>
          ))}
        </select>
        <span className="text-[10px] text-cream-3">
          The link stops working after this. You can also Revoke at any time.
        </span>
      </label>

      {error && <p className="text-xs text-rust">{error}</p>}

      <div className="flex items-center justify-end gap-2">
        <button type="button" className="btn btn-ghost text-xs" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary text-xs"
          disabled={busy || !label.trim() || (restrictBoards && selectedBoardIds.length === 0)}
        >
          {busy ? 'Creating…' : 'Create share link'}
        </button>
      </div>
    </form>
  );
}

function ShareRow({
  share,
  boards,
  onRevoke,
}: {
  share: CalendarShareSummary;
  boards: BoardSummary[];
  onRevoke: (id: string, label: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState<{
    events: CalendarSharePreviewEvent[];
    todos: CalendarSharePreviewTodo[];
  } | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const boardLabel =
    share.boardIds === null
      ? 'All boards'
      : share.boardIds.length === 0
        ? 'No boards'
        : share.boardIds
            .map((id) => boards.find((b) => b.id === id)?.name ?? '(removed)')
            .join(', ');

  const lensLabel = LENS_OPTIONS.find((o) => o.value === share.lens)?.label ?? share.lens;

  function copy() {
    void navigator.clipboard?.writeText(share.url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  }

  async function togglePreview() {
    if (preview) {
      setPreview(null);
      return;
    }
    setPreviewBusy(true);
    try {
      const p = await api.previewCalendarShare(share.id);
      setPreview(p);
    } finally {
      setPreviewBusy(false);
    }
  }

  const expiresLabel = share.expiresAt
    ? `Expires ${formatDate(share.expiresAt)}`
    : 'No expiry';
  const lastAccessLabel = share.lastAccessAt
    ? `Last viewed ${relativeSeen(share.lastAccessAt)}${
        share.lastAccessUa ? ` via ${uaPretty(share.lastAccessUa)}` : ''
      }`
    : 'Not subscribed yet';

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-ink-rim bg-ink-3/40 p-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-medium">{share.label}</span>
            {share.audience && (
              <span className="text-xs text-cream-3">→ {share.audience}</span>
            )}
            <span className="chip">{lensLabel}</span>
          </div>
          <div className="mt-1 text-[11px] text-cream-3">
            {boardLabel} · {expiresLabel} · {lastAccessLabel}
            {share.uniqueSubscribers > 0 && ` · ${share.uniqueSubscribers} subscriber${share.uniqueSubscribers === 1 ? '' : 's'}`}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-1 text-[11px]"
            onClick={() => void togglePreview()}
            disabled={previewBusy}
            title="See what the recipient sees"
          >
            <IconEye size={12} />
            {preview ? 'Hide preview' : previewBusy ? 'Loading…' : 'Preview'}
          </button>
          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-1 text-[11px]"
            onClick={copy}
            title="Copy the share URL"
          >
            <IconCopy size={12} />
            {copied ? 'Copied' : 'Copy URL'}
          </button>
          {share.audience && /@/.test(share.audience) && (
            <a
              href={composeMailto(share)}
              className="btn btn-ghost inline-flex items-center gap-1 text-[11px]"
              title={`Open an email to ${share.audience} with the link pre-filled`}
            >
              Send to {share.audience.split('@')[0]}
            </a>
          )}
          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-1 text-[11px] text-rust"
            onClick={() => onRevoke(share.id, share.label)}
            title="Stop access for everyone subscribed to this link"
          >
            <IconTrash size={12} />
            Revoke
          </button>
        </div>
      </div>
      <code className="block truncate rounded-md bg-ink-2 px-2.5 py-1.5 text-[11px]">
        {share.url}
      </code>
      {preview && (
        <div className="rounded-md border border-ink-rim bg-ink-2 p-2.5">
          <div className="mb-1.5 text-[10px] uppercase tracking-wide text-cream-3">
            What {share.audience ?? 'they'} will see
          </div>
          {preview.events.length === 0 && preview.todos.length === 0 ? (
            <p className="text-[11px] text-cream-3">No upcoming items match this filter.</p>
          ) : (
            <ul className="flex flex-col gap-1 text-[11px]">
              {preview.events.slice(0, 10).map((e, i) => (
                <li key={`e${i}`} className="flex items-center gap-2">
                  <span className="text-cream-3">{formatDateTime(e.startsAt)}</span>
                  <span className="flex-1 truncate">{e.title}</span>
                  {e.status === 'TENTATIVE' && (
                    <span className="chip text-[9px]">tentative</span>
                  )}
                </li>
              ))}
              {preview.todos.slice(0, 5).map((t, i) => (
                <li key={`t${i}`} className="flex items-center gap-2 text-cream-3">
                  <span>☐</span>
                  <span className="flex-1 truncate">{t.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function composeMailto(share: CalendarShareSummary): string {
  const subject = `Subscribe to my calendar — ${share.label}`;
  // Most calendar apps recognise webcal:// as "add to calendar" — pass
  // BOTH so the recipient gets a one-click subscribe AND a copyable
  // URL if their client doesn't honour the scheme.
  const webcal = share.url.replace(/^https?:\/\//, 'webcal://');
  const lensLine =
    share.lens === 'busy'
      ? `It only shows my busy/free time — no details.`
      : share.lens === 'headlines'
        ? `It only shows the milestones — kickoffs, demos, key dates.`
        : `It has the full schedule, with links back to context where I have it.`;
  const body =
    `Hi,\n\n` +
    `Here's a read-only calendar feed from SynFolio. ${lensLine}\n\n` +
    `Add to calendar:\n${webcal}\n\n` +
    `Or paste this URL into your calendar app:\n${share.url}\n\n` +
    `I can revoke it any time from my end.`;
  return `mailto:${encodeURIComponent(share.audience ?? '')}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}

function uaPretty(ua: string): string {
  if (ua === 'apple') return 'Apple Calendar';
  if (ua === 'google') return 'Google Calendar';
  if (ua === 'outlook') return 'Outlook';
  return 'a calendar app';
}

function formatDate(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(unix: number): string {
  const d = new Date(unix * 1000);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ---- helpers ------------------------------------------------------------

/**
 * Tag an error with its HTTP status when we have one so the inline
 * "Couldn't load X" copy points at the real cause — auth (401),
 * server (500), missing route (404), etc. — without forcing the
 * user into devtools.
 */
function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.message} (HTTP ${e.status})`;
  }
  if (e instanceof Error) return e.message;
  return 'Failed';
}

function guessTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function formatMinute(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (m === 24 * 60) return '24:00';
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function relativeSeen(unixSec: number): string {
  const d = Math.floor(Date.now() / 1000) - unixSec;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

const COMMON_TZS = [
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
];
